import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Gift, ExternalLink, Check, RotateCcw } from 'lucide-react';

export default function WishesPage() {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    apiGet('/api/wishes')
      .then((r) => setWishes(r.data.wishes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleClaim = async (wishId) => {
    try {
      await apiPost(`/api/wishes/${wishId}/claim`, {});
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Allerede reserveret');
    }
  };

  const handleUnclaim = async (wishId) => {
    try {
      await apiPost(`/api/wishes/${wishId}/unclaim`, {});
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Kunne ikke fjerne reservation');
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="wishes-page">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Ønskeliste
      </h1>

      <p className="text-sm text-slate-400 mb-8">
        Gaveidéer og ønsker til fejringen. Reserver et ønske, så andre kan se, at du har styr på det.
      </p>

      {loading ? (
        <p className="text-sm text-slate-400">Indlæser...</p>
      ) : wishes.length === 0 ? (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-8 text-center">
          <Gift className="w-12 h-12 text-pink-400/40 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Der er ikke tilføjet ønsker endnu. Arrangøren tilføjer dem senere.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {wishes.map((w) => (
            <div
              key={w.id}
              className={`bg-slate-800/40 border rounded-xl p-5 transition-all ${
                w.claimed ? 'border-emerald-500/20 opacity-70' : 'border-slate-700/50'
              }`}
              data-testid={`wish-${w.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-medium text-slate-200" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {w.title}
                    </h3>

                    {w.claimed && (
                      <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Reserveret
                      </span>
                    )}
                  </div>

                  {w.description && <p className="text-sm text-slate-400 mt-1">{w.description}</p>}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:flex-shrink-0">
                  {w.link && (
                    <a
                      href={w.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex justify-center p-3 sm:p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-blue-400 transition-colors border border-slate-700/50 sm:border-0"
                      data-testid={`wish-link-${w.id}`}
                      aria-label="Åbn ønskelink"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}

                  {!w.claimed ? (
                    <Button
                      size="sm"
                      onClick={() => handleClaim(w.id)}
                      className="w-full sm:w-auto bg-pink-600/80 hover:bg-pink-500 text-white text-xs"
                      data-testid={`claim-wish-${w.id}`}
                    >
                      <Gift className="w-3.5 h-3.5 mr-1" />
                      Reserver
                    </Button>
                  ) : w.claimed_by_me ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUnclaim(w.id)}
                      className="w-full sm:w-auto border-slate-600 text-slate-300 hover:bg-slate-700 text-xs"
                      data-testid={`unclaim-wish-${w.id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" />
                      Fortryd
                    </Button>
                  ) : (
                    <span className="text-xs px-3 py-2 rounded bg-slate-700/50 text-slate-400 text-center">
                      Reserveret
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
